import React, { useMemo, useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import '@solana/wallet-adapter-react-ui/styles.css';
import { Toaster } from './components/ui/sonner';
import { WalletAuthProvider } from './contexts/WalletAuthContext';
import { installQuotaFriendlyInterceptor } from './lib/quotaInterceptor';

// Phase L-TOOLS-POLISH — translate backend 429 QUOTA_EXCEEDED
// responses into a single friendly toast across every tool.
installQuotaFriendlyInterceptor();

// Eager: MissionControl loads instantly (root/homepage, must be fast)
import MissionControl from './pages/MissionControl';
import ComingSoon from './components/ComingSoon';
import Teaser from './components/Teaser';

// Lazy: All other pages load on-demand
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
// HomePage source retained but no longer part of the public entry flow (MC1 V3 §17).
const Settings = lazy(() => import('./pages/Settings'));
const CollageLanding = lazy(() => import('./pages/CollageLanding'));
const CollageCreate = lazy(() => import('./pages/CollageCreate'));
const SplitterLanding = lazy(() => import('./pages/SplitterLanding'));
const SplitterCreate = lazy(() => import('./pages/SplitterCreate'));
const AirdropperLanding = lazy(() => import('./pages/AirdropperLanding'));
const AirdropperCreate = lazy(() => import('./pages/AirdropperCreate'));
const EliminationLanding = lazy(() => import('./pages/EliminationLanding'));
const EliminationLive = lazy(() => import('./pages/EliminationLive'));
const EliminationLobby = lazy(() => import('./pages/EliminationLobby'));
const EliminationDashboard = lazy(() => import('./pages/EliminationDashboard'));
const EliminationLink = lazy(() => import('./pages/EliminationLink'));
const RevenueDashboard = lazy(() => import('./pages/RevenueDashboard'));

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#9945FF] border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  const endpoint = useMemo(() => process.env.REACT_APP_SOLANA_RPC_URL, []);

  // RTL-WALLET-SELECTOR (2026-08-24) — DO NOT pre-register classic
  // adapters. Phantom, Backpack, Solflare (and every mainstream Solana
  // wallet from 2024 onward) inject themselves via the Wallet Standard
  // and are auto-discovered by `@solana/wallet-adapter-react`. Passing
  // `PhantomWalletAdapter()` alongside a Wallet-Standard Phantom
  // created TWO adapter instances both named "Phantom" — the classic
  // one reads `window.solana`, which Backpack routinely hijacks when
  // set as the browser's default Solana wallet, so clicking Phantom
  // in the modal could dispatch `connect()` to Backpack. Same effect
  // for the sign-message path. Fix: rely on Wallet Standard, so each
  // wallet in the modal is a distinct adapter bound to its OWN
  // injected provider (not the shared `window.solana` object).
  const wallets = useMemo(() => [], []);

  // RTL-WALLET-SELECTOR — one-time cleanup for users whose browser has
  // a legacy `walletName` localStorage value pointing at the classic
  // adapter that no longer exists in our `wallets` array. Wallet-
  // Standard adapters use the same display names ("Phantom",
  // "Backpack", "Solflare"), so `select(walletName)` will re-resolve
  // cleanly — but a stray name such as "PhantomWalletAdapter" (older
  // wallet-adapter versions) could point at nothing and silently break
  // autoConnect. Clearing on first mount avoids that class of ghost.
  useEffect(() => {
    try {
      const KEY = 'walletName';
      const cur = window.localStorage.getItem(KEY);
      // Only clear obviously stale/legacy internal-name variants.
      // Standard names ('Phantom', 'Backpack', 'Solflare', etc.) are
      // preserved so the user's last explicit choice restores as
      // expected.
      if (cur && /Adapter$/.test(cur)) {
        window.localStorage.removeItem(KEY);
      }
    } catch { /* localStorage disabled — no-op */ }
  }, []);

  return (
    <ConnectionProvider endpoint={endpoint}>
      {/* autoConnect is REQUIRED. Without it, selecting a wallet from the
          wallet-adapter-react-ui modal only calls select(); adapter.connect()
          is never invoked, so `connected` stays false and Mission Control
          reverts to "Connect Wallet" the moment the modal closes. Restoring
          the standard wallet-adapter recommendation. See MC1 wallet-connect
          defect investigation. RTL-WALLET-SELECTOR (2026-08-24): with an
          empty `wallets` array, autoConnect only fires against the
          Wallet-Standard adapter the user last explicitly selected — no
          classic-adapter shadow to override it. */}
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          <WalletAuthProvider>
          <div className="App">
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Root — Mission Control is the Degens DAO homepage (MC1 V3 §1/§17).
                      Disconnected visitors see the ecosystem; wallet connection
                      personalises capabilities without gating the view. */}
                  <Route path="/" element={<MissionControl />} />

                  {/* Mission Control — canonical alias route retained. */}
                  <Route path="/mission-control" element={<MissionControl />} />
                  {/* Poker teaser — corrected approved artwork (clean
                      typography baked in; no CSS mask required). */}
                  <Route path="/mission-control/poker" element={
                    <Teaser
                      imageSrc="/mc/poker-teaser-approved.png"
                      imageAlt="Degens Poker — Not yet, Degen. We'll let you know when we're ready for you here."
                      accent="#F5C453"
                      testId="poker-teaser-page"
                    />
                  } />
                  <Route path="/mission-control/governance" element={<MissionControl />} />

                  {/* SolTracker Tool (Mainnet). Canonical entry is
                      /soltracker/dashboard; naked /dashboard is a
                      backward-compat alias that now redirects to
                      Mission Control per MC1 §25 safety finding. */}
                  <Route path="/soltracker" element={<LandingPage />} />
                  <Route path="/soltracker/dashboard" element={<Dashboard />} />
                  <Route path="/soltracker/settings" element={<Settings />} />
                  <Route path="/dashboard" element={<Navigate to="/mission-control" replace />} />
                  <Route path="/settings" element={<Settings />} />
                  
                  {/* NFT Collage Tool (Mainnet) */}
                  <Route path="/collage" element={<CollageLanding />} />
                  <Route path="/collage/create" element={<CollageCreate />} />
                  
                  {/* Wallet Splitter Tool (Mainnet) */}
                  <Route path="/splitter" element={<SplitterLanding />} />
                  <Route path="/splitter/create" element={<SplitterCreate />} />
                  
                  {/* Airdropper Tool (Mainnet) */}
                  <Route path="/airdropper" element={<AirdropperLanding />} />
                  <Route path="/airdropper/create" element={<AirdropperCreate />} />
                  
                  {/* P2P Trade Beta holding page — approved artwork rendered
                      inside the Mission Control shell. Devnet-only until the
                      security programme completes. */}
                  <Route path="/p2p-trade" element={
                    <Teaser
                      imageSrc="/mc/p2p-beta-approved.png"
                      imageAlt="P2P Trade — Beta Testing. Secure trades, Solana assets, no middlemen."
                      accent="#5AA9FF"
                      testId="p2p-teaser-page"
                    />
                  } />
                  <Route path="/p2p-trade/create" element={
                    <Teaser
                      imageSrc="/mc/p2p-beta-approved.png"
                      imageAlt="P2P Trade — Beta Testing. Secure trades, Solana assets, no middlemen."
                      accent="#5AA9FF"
                      testId="p2p-teaser-page"
                    />
                  } />
                  <Route path="/p2p-trade/:tradeId" element={
                    <Teaser
                      imageSrc="/mc/p2p-beta-approved.png"
                      imageAlt="P2P Trade — Beta Testing. Secure trades, Solana assets, no middlemen."
                      accent="#5AA9FF"
                      testId="p2p-teaser-page"
                    />
                  } />

                  {/* Elimination Game — polished set of Sora 2 suspense clips + per-outcome stills */}
                  <Route path="/elimination-game" element={<EliminationLanding />} />
                  <Route path="/elimination-game/live" element={<EliminationLive />} />
                  <Route path="/elimination-game/lobby/:tournamentId" element={<EliminationLobby />} />
                  <Route path="/elimination-game/dashboard" element={<EliminationDashboard />} />

                  {/* Phase E1B — Discord ↔ Wallet magic-link redemption + signature */}
                  <Route path="/elimination-link" element={<EliminationLink />} />
                  
                  {/* Admin - Revenue Dashboard */}
                  <Route path="/dao/revenue" element={<RevenueDashboard />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            <Toaster position="bottom-right" theme="dark" />
          </div>
          </WalletAuthProvider>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}

export default App;
